import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { api, resolveImage } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import Seo from "@/components/Seo";

const CATEGORY_ACCENT = {
  "Brand Activation": "bg-ot-peach",
  "Event Technology": "bg-ot-mint",
  "IT & AV Rental": "bg-ot-coral",
};

// Per-slug SEO keyword overrides — surfaces target keywords for high-intent products
const PRODUCT_SEO = {
  "online-registration": {
    title: "Online Event Registration Belgium — Branded Pages with Payments",
    descBoost: "Custom event registration platform in Belgium: branded sign-up flows, payment integration, GDPR-safe data capture and automated reminders.",
    keywords: "event registration belgium, online event registration, branded registration pages, eventbrite alternative belgium",
  },
  "onsite-registration-badging": {
    title: "Onsite Badging Belgium — Self Check-in & QR Badge Printing | OneTec",
    descBoost: "Onsite badging in Belgium with award-winning self check-in kiosks and on-demand PVC/paper badge printing — 1000+ attendees/hour.",
    keywords: "onsite badging belgium, event badge printing, self check-in kiosk, PVC badge belgium",
  },
  "access-control": {
    title: "Event Access Control & Accreditation Systems Belgium | OneTec",
    descBoost: "Event accreditation systems and access control in Belgium: QR/RFID scanning, multi-zone, live dashboards, offline fallback. From 500 to 50,000 attendees.",
    keywords: "event accreditation systems, event access control belgium, RFID access control, multi-zone event access",
  },
  "the-incredible-vespa": {
    title: "Vespa Photobooth Rental Belgium — Vintage Photo Activation | OneTec",
    descBoost: "Rent the iconic Vespa photobooth in Belgium — branded prints in seconds, guaranteed queue-maker for events.",
    keywords: "photobooth rental belgium, vespa photobooth, vintage photobooth, branded photobooth",
  },
  "the-amsterdammer": {
    title: "Amsterdammer Cargo Bike Photobooth Rental | Belgium",
    descBoost: "Outdoor photobooth rental in Belgium — wooden Dutch cargo bike, custom branding, instant prints.",
    keywords: "photobooth rental belgium, outdoor photobooth, cargo bike activation",
  },
  "the-120-experience": {
    title: "120° Bullet-Time Photobooth Rental Belgium — Matrix-Style | OneTec",
    descBoost: "Rent the 120° bullet-time camera rig in Belgium for cinematic, Matrix-style branded videos at your event.",
    keywords: "120 degree photobooth, bullet time photobooth belgium, brand activation belgium",
  },
  "the-mosaic-photo-experience": {
    title: "Mosaic Photo Experience — Brand Activation Belgium | OneTec",
    descBoost: "Live-growing photo mosaic that reveals your brand logo in Belgium — selfie-driven engagement magnet.",
    keywords: "brand activation belgium, photo mosaic, selfie wall",
  },
  "studio-from-the-sky": {
    title: "Studio from the Sky — Top-Down Photobooth Rental | Belgium",
    descBoost: "Surreal top-down photo studio rental in Belgium — guests appear to fly, float or swim. Cinematic branded imagery.",
    keywords: "photobooth rental belgium, top-down photo studio, brand activation",
  },
  "the-face-swap": {
    title: "AI Face Swap Photobooth — Brand Activation Belgium | OneTec",
    descBoost: "AI face swap activation in Belgium — guests become rockstars, astronauts or athletes in seconds. Shareable branded videos.",
    keywords: "brand activation belgium, ai face swap, ai photobooth",
  },
  "the-virtual-experience": {
    title: "VR Brand Activation Belgium — Immersive Branded Experiences | OneTec",
    descBoost: "Custom VR brand activations in Belgium — multi-headset setups, branded virtual worlds, social sharing.",
    keywords: "brand activation belgium, vr experience, virtual reality activation",
  },
  "webinars": {
    title: "Webinar & Hybrid Event Platform Belgium | OneTec",
    descBoost: "Premium webinars and hybrid event production in Belgium — live streaming, Q&A, polls, multi-language, full recording.",
    keywords: "webinar belgium, hybrid events, virtual events belgium",
  },
  "lead-trackers": {
    title: "Lead Tracker App for Exhibitors Belgium | OneTec",
    descBoost: "Lead capture apps for exhibitors in Belgium — real-time scan, qualify, CRM export, offline mode.",
    keywords: "lead tracker belgium, exhibitor lead capture, trade show lead scanner",
  },
  "mobile-voting-system": {
    title: "Mobile Voting & Polling Belgium — Live Engagement | OneTec",
    descBoost: "Live audience voting and polling system in Belgium for AGMs, conferences and panel debates. Instant on-stage results.",
    keywords: "mobile voting belgium, audience response system, live polling",
  },
  "laptop-rental": {
    title: "Laptop Rental Belgium — Pre-Configured & Insured | OneTec",
    descBoost: "Short-term laptop rental in Belgium and pan-European — pre-configured, insured, delivered with on-site support.",
    keywords: "laptop rental belgium, short term it rental, training laptop rental",
  },
  "screen-av-rental": {
    title: "LED Wall & AV Rental Belgium — Screens, Sound & Lights | OneTec",
    descBoost: "AV rental in Belgium: LED walls, 4K screens, projectors, sound systems. Design, deliver, install, breakdown.",
    keywords: "av rental belgium, led wall rental, screen rental",
  },
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageTitle("products", p?.name || "Product");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/products/${slug}`);
        if (!cancelled) setP(data);
      } catch (e) {
        if (!cancelled) setError("Product not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <div className="h-96 bg-white/50 nb-card animate-pulse" />
      </div>
    );
  }

  if (error || !p) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 text-center" data-testid="product-not-found">
        <h1 className="font-display text-5xl uppercase">Not found</h1>
        <Link to="/products" className="nb-btn mt-6"><ArrowLeft weight="bold" /> Back to products</Link>
      </div>
    );
  }

  const accent = p.accent_color || "#f0cfb0";
  const seo = PRODUCT_SEO[p.slug] || {};
  const seoTitle = seo.title || `${p.name} — ${p.category} Belgium | OneTec`;
  const seoDesc = seo.descBoost
    ? `${seo.descBoost} ${p.short_description}`
    : `${p.short_description} OneTec — event technology in Belgium since 2002.`;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://onetec.eu";

  return (
    <div data-testid="product-detail-page">
      <Seo
        title={seoTitle}
        path={`/products/${p.slug}`}
        description={seoDesc}
        image={p.image_url}
        type="article"
        noTemplate
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              name: p.name,
              description: p.description,
              image: p.image_url,
              category: p.category,
              brand: { "@type": "Brand", name: "OneTec" },
              offers: {
                "@type": "Offer",
                availability: "https://schema.org/InStock",
                areaServed: { "@type": "Country", name: "Belgium" },
                seller: { "@type": "Organization", name: "OneTec SRL" },
                url: `${origin}/products/${p.slug}`,
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
                { "@type": "ListItem", position: 2, name: "Products", item: `${origin}/products` },
                { "@type": "ListItem", position: 3, name: p.name, item: `${origin}/products/${p.slug}` },
              ],
            },
          ],
        }}
      />
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm font-display uppercase hover:bg-ot-peach px-2 py-1 -ml-2" data-testid="back-to-products">
            <ArrowLeft weight="bold" /> All products
          </Link>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-16 grid md:grid-cols-12 gap-8 md:gap-12 items-start">
        <div className="md:col-span-7">
          <div
            className="relative border-2 border-ot-fg shadow-[12px_12px_0px_#111111] overflow-hidden"
            style={{ backgroundColor: accent }}
          >
            <img src={resolveImage(p.image_url)} alt={p.name} className="w-full aspect-square object-cover mix-blend-multiply" />
          </div>
        </div>

        <div className="md:col-span-5">
          <span className={`inline-block ${CATEGORY_ACCENT[p.category] || "bg-ot-muted"} text-ot-fg text-xs uppercase tracking-widest font-bold px-2 py-1 border-2 border-ot-fg`}>
            {p.category}
          </span>
          <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9] mt-4" data-testid="product-title">
            {p.name}
          </h1>
          <p className="mt-5 text-xl text-ot-fg/80" data-testid="product-short-desc">{p.short_description}</p>
          <p className="mt-6 text-lg leading-relaxed text-ot-fg/90">{p.description}</p>

          <div className="mt-8">
            <p className="font-display text-sm uppercase tracking-widest text-ot-fg/50 mb-4">What's inside</p>
            <ul className="space-y-3">
              {p.features?.map((f) => (
                <li key={f} className="flex gap-3 items-start">
                  <CheckCircle size={22} weight="fill" className="text-ot-fg mt-1 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to={`/contact?product=${encodeURIComponent(p.name)}`}
              className="nb-btn"
              data-testid="product-cta-contact"
            >
              Get a quote <ArrowUpRight weight="bold" />
            </Link>
            <Link to="/products" className="nb-btn nb-btn-ghost">More products <ArrowUpRight weight="bold" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
